// ============================================================================
// SingleImportForm ViewModel
// ============================================================================

import { useContext, useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { UserContext } from "../../../../hooks/UserContext";
import { API } from "../../../../api";
import { Job, type Contact } from "../../../../models";
import { Oops } from "../../../../utilities/oops";
import { CREATE_NEW_GROWER, CREATE_NEW_SALESPERSON } from "../../../../utilities/constants";
import { isFieldVisible, validateJobs, ValidationContext } from "../../../../utilities/jobValidation";
import type { ImportTableColumn } from "../../../../utilities/types";
import type { ColumnContext } from "../../ImportTable/ImportTable/columns/standard-column-types/ColumnContext";
import { findDeal } from "../../ImportTable/ImportTable/ImportTable.helpers";
import { useDropdownOptions } from "../../shared/useDropdownOptions";
import { ManualEntryFormProps, SingleImportFormViewModel } from "./SingleImportForm.types";
import { commaStringToUniqueAlphanumericList } from "./SingleImportForm.helpers";

/**
 * Get default form values for job creation
 * Single source of truth for both initial form state and reset values
 */
function getDefaultFormValues(
	props: ManualEntryFormProps,
	userContext: { email?: string; full_name?: string } | undefined,
): Job {
	const defaults = Job.defaultForImport();
	return {
		...defaults,
		growerIds: [CREATE_NEW_GROWER],
		submitterEmail: userContext?.email || "",
		submitterName: userContext?.full_name || "",
		branchIds: (props.selectedBranches?.length ?? 0) > 0 ? [props.selectedBranches![0].id] : [],
		dealIds: props.selectedDeal ? [props.selectedDeal.id] : ["custom"],
		importDensity: props.selectedDeal?.density ?? defaults.importDensity,
		importDepth: props.selectedDeal?.depth ?? defaults.importDepth,
		externalTestPackageId: props.selectedDeal?.testPackage ?? defaults.externalTestPackageId,
		externalFrequencyOfMicros: props.selectedDeal?.frequencyOfMicros ?? defaults.externalFrequencyOfMicros,
	} as Job;
}

export function useSingleImportFormViewModel(props: ManualEntryFormProps): SingleImportFormViewModel {
	const userContext = useContext(UserContext);

	// State
	const [loading, setLoading] = useState(false);
	const [newSalespersons, setNewSalespersons] = useState<Contact[]>([]);
	const [readySelectionState, setReadySelectionState] = useState<Record<string, boolean>>({});
	const [localValidationErrors, setLocalValidationErrors] = useState<Record<string, string>>({});

	// React Hook Form
	const { watch, setValue, reset } = useForm<Job>({
		defaultValues: getDefaultFormValues(props, userContext),
	});

	const rawJob = watch() as Job;
	// Stabilize: watch() returns new ref every render. Only update when any field actually changes.
	const currentJobRef = useRef(rawJob);
	const prev = currentJobRef.current;
	if (Object.keys(rawJob).some((k) => rawJob[k as keyof Job] !== prev[k as keyof Job])) {
		currentJobRef.current = rawJob;
	}
	const currentJob = currentJobRef.current;

	// Auto-populate deal fields on mount if deal is selected
	useEffect(() => {
		const dealId = currentJob.dealIds?.[0];
		if (dealId && dealId !== "custom") {
			const deal = props.allDeals.find((d) => d.id === dealId);
			if (deal) {
				const needsDensity = !currentJob.importDensity || currentJob.importDensity === 0;
				const needsDepth = !currentJob.importDepth || currentJob.importDepth === 0;
				if (needsDensity || needsDepth) {
					const populatedJob = { ...currentJob };
					Job.updateFromDeal(populatedJob, deal);
					reset(populatedJob);
				}
			}
		}
	}, []); // Only on mount

	// Dropdown options from deals (shared hook)
	const dropdownOptions = useDropdownOptions({ allDeals: props.allDeals, job: currentJob });

	// Validation context
	const validationContext: ValidationContext = useMemo(
		() => ({
			company: props.company ?? undefined,
			deal: undefined, // Will be derived from current job state
			allDeals: props.allDeals,
			job: currentJob,
			user: userContext ?? undefined,
			hasPointsFile: !!currentJob.files?.pointsGeoJSON?.features?.length,
			isCreatingNewSalesperson: currentJob.salespersonIds?.[0] === CREATE_NEW_SALESPERSON,
		}),
		[props.company, props.allDeals, currentJob, userContext],
	);

	// Run validation when job changes
	useEffect(() => {
		const timer = setTimeout(() => {
			const errors = validateJobs([currentJob], {
				...validationContext,
				job: currentJob,
			});
			setLocalValidationErrors(errors);
		}, 0);
		return () => clearTimeout(timer);
	}, [currentJob, validationContext]);

	// Helper functions for column context
	const isNewSalesperson = (id: string): boolean => id.startsWith("temp-");
	const findNewSalesperson = (id: string): Contact | undefined => newSalespersons.find((sp) => sp.id === id);

	// Column context for mobile columns - follows JobImportForm.vm.ts pattern
	const columnContext: ColumnContext = useMemo(() => {
		const setJobs = (updater: Job[] | ((prev: Job[]) => Job[])) => {
			const jobs = [currentJob];
			const updated = typeof updater === "function" ? updater(jobs) : updater;
			const updatedJob = updated[0];

			if (updatedJob) {
				Object.keys(updatedJob).forEach((key) => {
					const typedKey = key as keyof Job;
					const newValue = updatedJob[typedKey];
					const oldValue = currentJob[typedKey];
					if (newValue !== oldValue) {
						setValue(typedKey, newValue as any);
					}
				});
			}
		};

		const validateJobsFn = (jobsToValidate: Job[]) => {
			const errors = validateJobs(jobsToValidate, {
				...validationContext,
				job: jobsToValidate[0],
			});
			setLocalValidationErrors(errors);
			return errors;
		};

		return {
			props: {
				allDeals: props.allDeals,
				selectedCompany: props.company,
				salespersons: props.salespersons,
				branches: props.branches,
				growers: props.growers,
				lastCropOptions: props.lastCropOptions,
				nextCropOptions: props.nextCropOptions,
			},
			jobs: [{ ...currentJob }],
			setJobs,
			validateJobs: validateJobsFn,
			validationContext,
			newSalespersons,
			setNewSalespersons,
			isNewSalesperson,
			findNewSalesperson,
			readySelectionState,
			setReadySelectionState,
			gridSizes: dropdownOptions.gridSizes,
			depths: dropdownOptions.depths,
			labPackages: dropdownOptions.labPackages,
			micros: dropdownOptions.micros,
			findDeal,
		} as unknown as ColumnContext;
	}, [
		currentJob,
		setValue,
		validationContext,
		props.allDeals,
		props.company,
		props.salespersons,
		props.branches,
		props.growers,
		props.lastCropOptions,
		props.nextCropOptions,
		newSalespersons,
		readySelectionState,
		dropdownOptions,
	]);

	// Helper to check field visibility
	const isVisible = useCallback(
		(columnId: ImportTableColumn) => isFieldVisible(columnId, validationContext),
		[validationContext],
	);

	// Check form validity
	const isFormValid = Object.keys(localValidationErrors).length === 0;

	// Submit handler
	const onSubmit = useCallback(async () => {
		// Run full validation
		const errors = validateJobs([currentJob], {
			...validationContext,
			job: currentJob,
		});
		setLocalValidationErrors(errors);

		if (Object.keys(errors).length > 0) {
			toast.error("Please fill in all required fields correctly.");
			return;
		}

		// Custom deal confirmation
		if (currentJob.dealIds?.at(0) === "custom") {
			if (!window.confirm("Custom Configuration Selected. Proceed?")) {
				return;
			}
		}

		setLoading(true);
		try {
			// Build the job to submit
			const jobToSubmit = { ...currentJob };

			// Clean sentinel values
			if (jobToSubmit.dealIds?.at(0) === "custom") {
				jobToSubmit.dealIds = [];
			}
			if (jobToSubmit.growerIds?.at(0) === CREATE_NEW_GROWER) {
				jobToSubmit.growerIds = [];
			}
			if (jobToSubmit.importGrowerName?.startsWith("New Grower: ")) {
				jobToSubmit.importGrowerName = jobToSubmit.importGrowerName.replace(/^New Grower: /, "");
			}
			if (jobToSubmit.salespersonIds?.includes(CREATE_NEW_SALESPERSON)) {
				jobToSubmit.salespersonIds = [];
			}

			// Parse microsRaw if present
			if (jobToSubmit.microsList) {
				jobToSubmit.microsList = commaStringToUniqueAlphanumericList(jobToSubmit.microsList);
			}

			// Update grower phone if existing grower selected
			if (jobToSubmit.growerIds?.length > 0 && jobToSubmit.growerIds[0] !== "" && jobToSubmit.growerPhoneNumber) {
				await API.Data.Growers.update.phoneNumber(jobToSubmit.growerIds[0], jobToSubmit.growerPhoneNumber);
			}

			await toast.loading("Job submitting...");
			await API.Data.Jobs.create.one(jobToSubmit, "Created Portal Single");
			await toast.dismiss();
			toast.success("Job submitted successfully!");

			// Reset form to default values
			const defaults = getDefaultFormValues(props, userContext);
			reset(defaults);
			setNewSalespersons([]);
		} catch (error: any) {
			await toast.dismiss();
			Oops.handleError(error, "SubmitJobs.SingleImportForm.onSubmit");
		} finally {
			setLoading(false);
		}
	}, [currentJob, validationContext, props, userContext, reset]);

	const updateJobFiles = useCallback(
		(jsonKey: string, value: unknown) => {
			// Set entire `files` object (not nested path) so the reference changes
			// and downstream memo checks in JobMap detect the update.
			setValue("files" as any, { ...currentJob.files, [jsonKey]: value });
		},
		[setValue, currentJob.files],
	);

	return {
		currentJob,
		columnContext,
		localValidationErrors,
		isVisible,
		isFormValid,
		dropdownOptions,
		branches: props.branches || [],
		loading,
		onSubmit,
		updateJobFiles,
		isSuperUser: userContext?.isAdmin ?? false,
	};
}
